import {useCallback, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';
import Controller from './Controller';
import PageViewer from './PageViewer';
import ScrollViewer from './ScrollViewer';
import Setting from './Setting';
import styles from './Viewer.module.scss';
import ViewerHeader from './ViewerHeader';
import EpisodeCommentModal from '../../comment/container/EpisodeCommentModal';
import {Page} from '../../common/constant/page';
import {pToast} from '../../common/container/PToast';
import useEffectOnChange from '../../common/hook/useEffectOnChange';
import useLogin from '../../common/hook/useLogin';
import useTypedSelector from '../../common/hook/useTypedSelector';
import {actions as commonActions} from '../../common/state/action';
import {useFetchInfo} from '../../common/util/fetch';
import {
  getTorosCustomProps,
  sendExtra,
  sendPageView,
} from '../../common/util/tracking';
import {setLastEpisode} from '../../commonSub/function';
import useViewerSettings from '../../commonSub/hook/useViewerSettings';
import {
  actions as commonSubActions,
  ActionType as CommonSubActionType,
} from '../../commonSub/state/action';
import {IUseTicketParams} from '../../commonSub/type';
import useLastPage, {getLastPage} from '../hook/useLastPage';
import useProgressRatio, {getProgressRatio} from '../hook/useProgressRatio';
import useViewerReadEvent from '../hook/useViewerReadEvent';
import {actions, ActionType} from '../state/actions';
import {IViewerContent, IViewerEpisode} from '../type';

interface IProps {
  episodeId: number;
  prevPage: Page;
  isSsr: boolean;
}

export default function Viewer({episodeId, prevPage, isSsr}: IProps) {
  const dispatch = useDispatch();
  const [
    episode,
    content,
    prevEpisode,
    nextEpisode,
    images,
    viewerState,
    isRunMode,
    autoScrollSpeed,
    isImageReady,
  ] = useTypedSelector(({viewer}) => [
    viewer.episode,
    viewer.content,
    viewer.prevEpisode,
    viewer.nextEpisode,
    viewer.images,
    viewer.viewerState,
    viewer.isRunMode,
    viewer.autoScrollSpeed,
    viewer.isImageReady,
  ]);
  const contentId = episode?.contentId;
  const isStopSales = !!episode?.isStopSalesEpisode || !!content?.isStopContent;
  const {t} = useTranslation();

  const [isFetchUserFinished, isAppMounted] = useTypedSelector(state => [
    state.auth.isFetchUserFinished,
    state.common.isAppMounted,
  ]);

  const [{isKeepLastPage, viewType}] = useViewerSettings(contentId);

  const {isFetched: isFetchedUseTicket} = useFetchInfo(
    CommonSubActionType.FetchUseTicket
  );

  useEffectOnChange(
    () => {
      if (content && episode && isSsr) {
        const {id, sid, type} = episode;
        const {id: contentId, sid: contentSid} = content;
        const params: IUseTicketParams = {
          contentId,
          contentSid,
          episodeId: id,
          episodeSid: sid,
          authModalTitleType: type === 'waitForFree' ? 'waitfree' : undefined,
          entryType: 'viewerDirect',
          isFree: type === 'free',
        };
        dispatch(commonSubActions.fetchUseTicket(params));
      }
    },
    isAppMounted && isFetchUserFinished && !!content && !!episode
  );

  const isRunModeRef = useRef(isRunMode);
  const [isHideController, setIsHideController] = useState(false);
  useEffect(() => {
    isRunModeRef.current = isRunMode;
  }, [isRunMode]);
  useEffect(() => {
    let timerId: number;
    if (isRunModeRef.current) {
      setIsHideController(true);
      timerId = window.setTimeout(() => {
        setIsHideController(false);
      }, CONTROLLBAR_TOGGLE_DELAY);
    } else {
      setIsHideController(false);
    }
    return () => {
      window.clearTimeout(timerId);
      setIsHideController(false);
      dispatch(actions.initViewerState());
    };
  }, [dispatch, episodeId]);

  const {isLogin, isLoginChecking} = useLogin();

  useEffect(() => {
    contentId &&
      !isLoginChecking &&
      !isLogin &&
      setLastEpisode({contentId, episodeId});
  }, [contentId, episodeId, isLogin, isLoginChecking]);

  const [initialPage, setInitialPage] = useState<number>(1);
  const initialPageRef = useRef(initialPage);
  const isKeepLastPageRef = useRef(isKeepLastPage);
  useEffect(() => {
    isKeepLastPageRef.current = isKeepLastPage;
  }, [isKeepLastPage]);
  useEffect(() => {
    if (!contentId) return;

    const lastPage =
      (isKeepLastPageRef.current && getLastPage(contentId, episodeId)) || 1;
    initialPageRef.current = lastPage;
    setInitialPage(lastPage);
  }, [contentId, episodeId, viewType]);

  const isCalledFetchResource = useRef<boolean>(false);
  useEffect(() => {
    return () => {
      isCalledFetchResource.current = false;
    };
  }, []);
  const {isFetched: isEpisodeFetched} = useFetchInfo(
    ActionType.FetchViewerEpisode,
    episodeId
  );
  useEffect(() => {
    return () => {
      dispatch(
        commonActions.removeFetchStatus([
          {actionType: ActionType.FetchResources, fetchKey: episodeId},
          {actionType: ActionType.FetchViewerData, fetchKey: episodeId},
        ])
      );
    };
  }, [dispatch, episodeId]);

  useEffect(() => {
    if (
      isEpisodeFetched &&
      contentId &&
      isFetchUserFinished &&
      !isCalledFetchResource.current &&
      (!isSsr || isFetchedUseTicket)
    ) {
      isCalledFetchResource.current = true;
      dispatch(
        actions.fetchResources({
          episodeId,
          contentId,
          initialPage: initialPageRef.current,
        })
      );
      dispatch(actions.fetchViewerData(contentId, episodeId));
    }
  }, [
    isSsr,
    contentId,
    dispatch,
    episodeId,
    isFetchUserFinished,
    isFetchedUseTicket,
    isEpisodeFetched,
  ]);

  useViewerReadEvent({
    contentId,
    episodeId,
    viewType,
    contentTitle: content?.title,
  });

  const {fetchStatus} = useFetchInfo(ActionType.FetchResources, episodeId);
  const viewTypeRef = useRef(viewType);
  useEffect(() => {
    viewTypeRef.current = viewType;
  }, [viewType]);
  useEffect(() => {
    if (fetchStatus === 'success') {
      sendPageView({
        section: 'viewer',
        page: 'viewer',
        actionName: 'viewer',
        customProps: {
          content_id: contentId, // 실제 로깅하진 않지만, 열람 전환 때 작품 아이디 비교를 위해 customProps 에 추가.
          episode_id: episodeId,
          viewer_type: viewTypeRef.current,
          is_binge: isRunModeRef.current ? 'Y' : 'N',
        },
      });
    }
  }, [contentId, episodeId, fetchStatus]);

  const {fetchStatus: viewerDataFetchStatus} = useFetchInfo(
    ActionType.FetchViewerData,
    episodeId
  );
  const torosInfo = useTypedSelector(
    state => state.viewer.viewerEnd?.recommendContents.torosInfo
  );
  useEffect(() => {
    if (viewerDataFetchStatus === 'success') {
      sendExtra({
        section: 'viewer',
        page: 'viewer',
        actionName: 'viewer',
        customProps: {
          content_id: contentId,
          episode_id: episodeId,
          ...(viewTypeRef.current === 'scroll' && !isRunModeRef.current
            ? getTorosCustomProps(torosInfo, {
                toros_page_meta_id: contentId,
                toros_action_type: 'Pageview',
              })
            : undefined),
          viewer_type: viewTypeRef.current,
          is_binge: isRunModeRef.current ? 'Y' : 'N',
        },
      });
    }
  }, [contentId, episodeId, torosInfo, viewerDataFetchStatus]);

  const lastPageFuncRef = useRef<() => void>();
  const ratioFuncRef = useRef<() => void>();

  // TODO: isKeepLastPage가 false => true로 바뀌면 현재 페이지를 저장
  const {saveLastPage} = useLastPage({contentId, episodeId});
  const lastPageCallbackId = useRef<number>();
  const onPageChanged = useCallback(
    (page: number) => {
      // TODO: 호출시 스크롤 끊김이 생긴다.
      lastPageFuncRef.current = () => saveLastPage(page);
      lastPageCallbackId.current &&
        window.cancelIdleCallback(lastPageCallbackId.current);
      lastPageCallbackId.current = window.requestIdleCallback(() =>
        saveLastPage(page)
      );
    },
    [saveLastPage]
  );

  const progressCallbackId = useRef<number>();
  const {saveProgressRatio} = useProgressRatio({contentId, episodeId});
  const onProgressChanged = useCallback(
    (ratio: number) => {
      // TODO: 호출시 스크롤 끊김이 생긴다.
      ratioFuncRef.current = () => saveProgressRatio(ratio);
      progressCallbackId.current &&
        window.cancelIdleCallback(progressCallbackId.current);
      progressCallbackId.current = window.requestIdleCallback(() => {
        saveProgressRatio(ratio);
      });
    },
    [saveProgressRatio]
  );

  useEffect(() => {
    return () => {
      lastPageFuncRef.current?.();
      lastPageFuncRef.current = undefined;
      ratioFuncRef.current?.();
      ratioFuncRef.current = undefined;
    };
  }, []);

  useEffect(() => {
    return () => {
      lastPageCallbackId.current &&
        window.cancelIdleCallback(lastPageCallbackId.current);
      lastPageCallbackId.current = undefined;
      progressCallbackId.current &&
        window.cancelIdleCallback(progressCallbackId.current);
      progressCallbackId.current = undefined;
    };
  }, []);

  const saveProgressRatioRef = useRef(saveProgressRatio);
  useEffect(() => {
    saveProgressRatioRef.current = saveProgressRatio;
  }, [saveProgressRatio]);
  useEffect(() => {
    if (!contentId) return;

    const ratio = getProgressRatio(contentId, episodeId);
    if (ratio === undefined) {
      saveProgressRatioRef.current(0);
    }
  }, [contentId, episodeId]);

  const onClickViewer = () => {
    if (autoScrollSpeed) {
      dispatch(actions.setValue('autoScrollSpeed', 0));
    } else if (viewerState === 'none') {
      dispatch(actions.setViewerState('default'));
    } else {
      dispatch(actions.setViewerState('none'));
    }
  };

  const callUseTicket = useCallback(
    ({
      content,
      episode,
    }: {
      content?: IViewerContent;
      episode?: IViewerEpisode;
    }) => {
      if (content && episode) {
        const authModalTitleType =
          episode.type === 'waitForFree' ? 'waitfree' : 'nextEpisode';
        dispatch(
          commonSubActions.fetchUseTicket({
            contentId: content.id,
            contentSid: content.sid,
            episodeId: episode.id,
            episodeSid: episode.sid,
            authModalTitleType,
            entryType: 'viewer',
            isFree: episode.type === 'free',
          })
        );
      }
    },
    [dispatch]
  );

  const isPrevInactive = !prevEpisode;
  const goToPrevEpisode = useCallback(() => {
    if (isPrevInactive) {
      pToast({
        desc: t('viewer_marathon_viewing_first_ep_toast'),
        variant: 'black',
      });
    } else {
      callUseTicket({content, episode: prevEpisode});
    }
  }, [callUseTicket, content, isPrevInactive, prevEpisode, t]);

  const isNextInactive = !nextEpisode;
  const goToNextEpisode = useCallback(() => {
    if (isNextInactive) {
      pToast({
        desc: t('viewer_marathon_viewing_last_ep_toast'),
        variant: 'black',
      });
    } else {
      callUseTicket({content, episode: nextEpisode});
    }
  }, [callUseTicket, content, isNextInactive, nextEpisode, t]);

  const onCloseComment = () => {
    dispatch(actions.setViewerState('default'));
  };

  const isShowViewer =
    isImageReady && episodeId === episode?.id && !!images.length;
  return (
    <div
      className={cn('w-full min-h-full overflow-x-hidden', {
        'h-full': viewType === 'page',
        [styles.checkBg]: !isShowViewer,
      })}
      onClick={onClickViewer}
    >
      <ViewerHeader
        title={episode?.title}
        ageLimit={episode?.ageLimit}
        contentId={contentId}
        episodeId={episodeId}
        viewType={viewType}
        prevPage={prevPage}
      />
      {contentId && (
        <Setting
          contentId={contentId}
          episodeId={episodeId}
          episodeTitle={episode?.title}
          isStopSales={isStopSales}
        />
      )}
      {isShowViewer && viewType === 'scroll' && (
        <ScrollViewer
          contentId={contentId}
          episodeId={episodeId}
          images={images}
          initialPage={initialPage}
          isRunMode={isRunMode}
          isStopSales={isStopSales}
          videoClip={episode?.videoClip}
          prevPage={prevPage}
          onProgressChanged={onProgressChanged}
          onPageChanged={onPageChanged}
          goToPrevEpisode={goToPrevEpisode}
          goToNextEpisode={goToNextEpisode}
        />
      )}
      {isShowViewer && viewType === 'page' && (
        <PageViewer
          contentId={contentId}
          episodeId={episodeId}
          images={images}
          initialPage={initialPage}
          isRunMode={isRunMode}
          isStopSales={isStopSales}
          videoClip={episode?.videoClip}
          direction={content?.pageViewDirection}
          onProgressChanged={onProgressChanged}
          onPageChanged={onPageChanged}
          goToPrevEpisode={goToPrevEpisode}
          goToNextEpisode={goToNextEpisode}
          prevPage={prevPage}
        />
      )}
      <Controller
        isPrevInactive={isPrevInactive}
        isNextInactive={isNextInactive}
        isStopSales={isStopSales}
        isHideController={isHideController}
        contentId={contentId}
        episodeId={episodeId}
        prevPage={prevPage}
        viewType={viewType}
        prevEpisode={prevEpisode}
        nextEpisode={nextEpisode}
        goToPrevEpisode={goToPrevEpisode}
        goToNextEpisode={goToNextEpisode}
      />
      {contentId && (
        <EpisodeCommentModal
          isOpen={viewerState === 'comment'}
          contentId={contentId}
          episodeId={episodeId}
          onRequestClose={onCloseComment}
        />
      )}
    </div>
  );
}

const CONTROLLBAR_TOGGLE_DELAY = 1000;
